import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMapContext } from '../context/MapContext';

const PropertyEditor = ({ showStatus }) => {
  const { t } = useTranslation();
  const { selectedFeature, updateFeatureProperties } = useMapContext();
  const [properties, setProperties] = useState({});
  
  // Update local state when selectedFeature changes
  useEffect(() => {
    if (selectedFeature && selectedFeature.feature) {
      setProperties({ ...selectedFeature.feature.properties });
    } else {
      setProperties({});
    }
  }, [selectedFeature]);
  
  // Handle property changes
  const handlePropertyChange = (key, value) => {
    setProperties(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  // Save properties
  const handleSave = () => {
    const success = updateFeatureProperties(properties);
    
    if (success) {
      showStatus(t('propertyEditor.messages.saveSuccess', 'Properties saved successfully'), 'success');
    } else {
      showStatus(t('propertyEditor.messages.saveError', 'Error saving properties'), 'error');
    }
  };
  
  // Get display label for property
  const getPropertyLabel = (propertyName) => {
    // Try to get the translation specifically for this field
    const translationKey = `propertyEditor.labels.${propertyName}`;
    const translation = t(translationKey);
    
    // If the translation key exists and returns a valid value, use it
    if (translation !== translationKey) {
      return translation;
    }
    
    // Fallback to the default field name formatting if no translation exists
    return propertyName.replace(/_/g, ' ');
  };
  
  // Define preferred field order
  const getOrderedFields = () => {
    // Define the order of fields to display
    const preferredOrder = [
      'DUN_NAME', 'DUN_CODE',
      'PARLIAMENT', 'PARLIAMENT_CODE',
      'STATE',
      'COALITION', 'COALITION_LABEL',
      'POLITICAL_PARTY',
      'ADUN_FULLNAME', 'ADUN_NICKNAME',
      'UNIQUE_ID',
      'FID', 'Id'
    ];
    
    // Get available fields in preferred order
    const orderedFields = preferredOrder.filter(field => field in properties);
    
    // Add any remaining fields not in the preferred order list
    const remainingFields = Object.keys(properties).filter(
      field => !preferredOrder.includes(field)
    );
    
    // Return all fields in order
    return [...orderedFields, ...remainingFields];
  };
  
  if (!selectedFeature) {
    return <p>{t('propertyEditor.selectFeature')}</p>;
  }
  
  return (
    <div className="feature-properties">
      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        {/* Display properties in the preferred order */}
        {getOrderedFields().map(prop => (
          <div className="property-field" key={prop}>
            <div className="property-name">{getPropertyLabel(prop)}</div>
            <input
              type="text"
              value={properties[prop] || ''}
              onChange={(e) => handlePropertyChange(prop, e.target.value)}
            />
          </div>
        ))}
        
        <button type="submit" className="btn btn-success">
          <i className="fas fa-save"></i> {t('propertyEditor.save')}
        </button>
      </form>
    </div>
  );
};

export default PropertyEditor;
